import type { Account, DashboardData, OwnerFilter, Transaction } from "../types";
import { isCard } from "./finance";
import { allocationCategoryList, categoryUseKeys, isUncategorizedExpense, payeeDigest } from "./coverage-digests";

// A dashboard payload may hold only the recent part of the ledger (see LedgerCoverage). These are
// the questions the client asks about that: what a screen needs, whether the payload has it, and
// the answers the server works out over the whole ledger so the client does not need the entries.

/** A lower month, a bounded `since..through` window, or `all`. */
export type Need = string | "all";

const MONTHS_BACK = 2;

/** The month the first load starts from: this month and the two before it. */
export function defaultSince(today: string): string {
  const [year, month] = today.slice(0, 7).split("-").map(Number);
  const index = year * 12 + (month - 1) - MONTHS_BACK;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
}

/** A stable string keeps React dependencies primitive while allowing a bounded window. */
export const boundedNeed = (since: string, through = since): Need => `${since}..${through}`;
export function needWindow(need: Need): { since: string; through?: string } | null {
  if (need === "all") return null;
  const [since, through] = need.split("..");
  return { since, ...(through ? { through } : {}) };
}

/** Smallest contiguous window that answers both requests. Legacy strings have no upper bound. */
export function widerNeed(a: Need, b: Need): Need {
  const left = needWindow(a), right = needWindow(b);
  if (!left || !right) return "all";
  const since = left.since < right.since ? left.since : right.since;
  return left.through && right.through ? boundedNeed(since, left.through > right.through ? left.through : right.through) : since;
}

/** A pinned transaction never implies coverage of its entire month. */
export function satisfies(data: Pick<DashboardData, "coverage">, need: Need): boolean {
  if (!data.coverage) return true;
  const window = needWindow(need);
  if (!window || data.coverage.since > window.since) return false;
  return !data.coverage.through || !!window.through && data.coverage.through >= window.through;
}

export const needOf = (data: Pick<DashboardData, "coverage">): Need => data.coverage
  ? data.coverage.through ? boundedNeed(data.coverage.since, data.coverage.through) : data.coverage.since : "all";

export { isUncategorizedExpense };

/** Uncategorised expenses per owner name, plus `All` — the shape `LedgerCoverage.uncategorized` carries. */
export function uncategorizedByOwner(transactions: Transaction[]): Record<string, number> {
  const counts: Record<string, number> = { All: 0 };
  for (const t of transactions) {
    if (!isUncategorizedExpense(t)) continue;
    counts.All += 1;
    counts[t.owner] = (counts[t.owner] ?? 0) + 1;
  }
  return counts;
}

export function uncategorizedCount(data: DashboardData, owner: OwnerFilter): number {
  if (data.coverage) return data.coverage.uncategorized[owner] ?? 0;
  return data.transactions.filter((t) => isUncategorizedExpense(t) && (owner === "All" || t.owner === owner)).length;
}

/** How many entries link to a payee, over the whole ledger. */
export const payeeEntryCounts = (data: Pick<DashboardData, "coverage" | "transactions">): Map<string, number> =>
  new Map(Object.entries(data.coverage ? data.coverage.payeeCounts : payeeDigest(data.transactions).payeeCounts));

/** What a payee's detail needs loaded: back to the payee's oldest entry. */
export function payeeNeed(data: Pick<DashboardData, "coverage">, payeeId: string): Need | null {
  return data.coverage?.payeeSince[payeeId] ?? null;
}

/** What the categorize review needs loaded: back to the oldest uncategorised expense. */
export function categorizeNeed(data: Pick<DashboardData, "coverage">): Need | null {
  return data.coverage?.uncategorizedSince || null;
}

/** Titles used on a trip: the loaded entries plus, on a windowed payload, those the older entries had. */
export function tripTitleList(data: Pick<DashboardData, "coverage" | "transactions">, tripId: string): string[] {
  const set = new Set(data.coverage?.tripTitles[tripId] ?? []);
  for (const t of data.transactions) if (t.tripId === tripId && t.title) set.add(t.title);
  return [...set].sort();
}

/** What the trip filter needs loaded: back to the trip's oldest entry. */
export function tripNeed(data: Pick<DashboardData, "coverage">, tripId: string): Need | null {
  if (!data.coverage) return null;
  return data.coverage.tripSince[tripId] ?? null;
}

/** The widest of these needs, ignoring those a payload cannot name; null when none is. */
export const widestNeed = (needs: (Need | null)[]): Need | null =>
  needs.reduce<Need | null>((wide, n) => (n && wide ? widerNeed(wide, n) : n ?? wide), null);

type Coverable = Pick<DashboardData, "coverage">;

/** What an account's detail needs loaded: back to the entries it lists. */
export const accountNeed = (data: Coverable, accountId: string): Need | null => data.coverage?.accountSince[accountId] ?? null;

/** What a loan's detail needs loaded: back to the loan's oldest entry. */
export const iouNeed = (data: Coverable, iouId: string): Need | null => data.coverage?.iouSince[iouId] ?? null;

/** What a bill's detail needs loaded: back to the bill's oldest entry. */
export const billNeed = (data: Coverable, billId: string): Need | null => data.coverage?.billSince[billId] ?? null;

/** What the screens that total these trips need loaded: back to the oldest entry on any of them. */
export const tripsNeed = (data: Coverable, tripIds: string[]): Need | null => widestNeed(tripIds.map((id) => tripNeed(data, id)));

/** Whether any entry, anywhere in the ledger, uses the account. */
export const accountHasEntries = (data: Pick<DashboardData, "coverage" | "transactions">, accountId: string): boolean =>
  data.coverage ? accountId in data.coverage.accountSince : data.transactions.some((t) => t.accountId === accountId || t.toAccountId === accountId);

/** The category and subcategory pairs entries use over the whole ledger, as `categoryUseKeys` writes them. */
export const categoryUse = (data: Pick<DashboardData, "coverage" | "transactions">): Set<string> =>
  new Set(data.coverage ? data.coverage.categoryUse : categoryUseKeys(data.transactions));

/** Every category any entry's allocation uses over the whole ledger, sorted. */
export const allocationCategories = (data: Pick<DashboardData, "coverage" | "transactions">): string[] =>
  data.coverage ? data.coverage.allocationCategories : allocationCategoryList(data.transactions);

/** Every month that has an entry, newest first. */
export function ledgerMonths(data: Pick<DashboardData, "coverage" | "transactions">): string[] {
  if (data.coverage) return data.coverage.months;
  return [...new Set(data.transactions.map((t) => t.date.slice(0, 7)))].sort().reverse();
}

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * What the screen being shown needs loaded. Anything not named here needs the whole ledger, so a
 * screen added later is correct before it is fast. Named screens read their selected month and
 * the digests in `LedgerCoverage`; what reaches further (a trip's entries, a payee's, a loan's) is
 * asked for on top of this, by `extraLedgerNeed` or by the screen once the thing is opened.
 */
export function ledgerNeed({ view, month, today }: {
  view: string;
  month: string;
  today: string;
}): Need {
  switch (view) {
    case "overview":
    case "insights":
    case "accounts":
    case "more":
    case "transactions":
    case "payees":
    case "ious":
    case "bills":
    case "trips":
    case "receipts":
    case "categories":
    case "budgets":
    case "account-settings":
    case "prices":
    case "achievements":
    case "settings":
    case "profile-settings":
    case "ai":
    case "staging-data":
    case "backups":
    case "errors":
      return boundedNeed(MONTH.test(month) ? month : today.slice(0, 7));
    default:
      return "all";
  }
}

/**
 * A card's repayment total counts everything since its statement was recorded, however long ago,
 * so the accounts screen needs entries back to the oldest such date.
 */
export function statementNeed(accounts: Account[]): Need | null {
  const months = accounts.filter((a) => isCard(a) && a.statementRecordedDate).map((a) => a.statementRecordedDate!.slice(0, 7));
  return months.length ? months.reduce((oldest, month) => (month < oldest ? month : oldest)) : null;
}
